/* Shopify Ajax Cart shipping estimates. No cart writes or address persistence. */
(() => {
  'use strict';

  const messages = {
    loading: 'Calculating shipping…',
    changed: 'Your address or cart changed. Calculate shipping again for an updated estimate.',
    empty: 'No shipping rates were found for this destination. Check your address or contact us for help.',
    error: 'Shipping could not be calculated. Check your destination and try again. Final shipping is shown at checkout.',
    timeout: 'Shipping is taking longer than expected. Please try again. Final shipping is shown at checkout.',
  };

  function abortError() {
    return new DOMException('Shipping request cancelled', 'AbortError');
  }

  function timeoutError() {
    const error = new Error(messages.timeout);
    error.name = 'ShippingTimeoutError';
    return error;
  }

  function endpoint(cartUrl, operation, address, origin) {
    const url = new URL(cartUrl, origin);
    if (url.origin !== origin || !/\/cart\/?$/.test(url.pathname)) {
      throw new Error('Invalid cart route');
    }
    url.pathname = `${url.pathname.replace(/\/$/, '')}/${operation}.json`;
    url.search = '';
    url.hash = '';
    for (const field of ['country', 'province', 'zip']) {
      url.searchParams.set(`shipping_address[${field}]`, String(address[field] || '').trim());
    }
    return url.href;
  }

  function pause(ms, signal) {
    return new Promise((resolve, reject) => {
      if (signal.aborted) return reject(abortError());
      const onAbort = () => {
        clearTimeout(timer);
        reject(abortError());
      };
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  async function getShippingRates({ cartUrl, address, origin, signal, fetchImpl = fetch,
    timeoutMs = 20000, pollMs = 1000, maxPolls = 15 }) {
    const controller = new AbortController();
    let timedOut = false;
    let timer;
    const cancel = () => controller.abort();
    const cancellation = new Promise((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(timedOut ? timeoutError() : abortError()), { once: true });
    });
    signal?.addEventListener('abort', cancel, { once: true });
    if (signal?.aborted) cancel();
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const request = async (operation, method) => {
      if (controller.signal.aborted) throw abortError();
      const response = await fetchImpl(endpoint(cartUrl, operation, address, origin), {
        method, credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (controller.signal.aborted) throw abortError();
      if (!response.ok) {
        // Fixed messages avoid echoing or recording a submitted address from API errors.
        const error = new Error(response.status === 429
          ? 'Too many shipping requests. Please wait a moment and try again.' : messages.error);
        error.name = 'ShippingRequestError';
        throw error;
      }
      return response;
    };

    const work = async () => {
      // A successful prepare response can be empty or null; it is not a rates response.
      await request('prepare_shipping_rates', 'POST');
      for (let attempt = 0; attempt < maxPolls; attempt += 1) {
        if (attempt > 0) await pause(pollMs, controller.signal);
        const response = await request('async_shipping_rates', 'GET');
        const payload = await response.json();
        if (controller.signal.aborted) throw abortError();
        if (payload === null) continue;
        if (payload && Array.isArray(payload.shipping_rates)) return payload.shipping_rates;
        throw new Error(messages.error);
      }
      throw timeoutError();
    };

    try {
      return await Promise.race([work(), cancellation]);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
    }
  }

  function formatRate(rate, fallbackCurrency, locale) {
    const rawPrice = rate.price;
    const amount = rawPrice === null || rawPrice === undefined || String(rawPrice).trim() === ''
      ? NaN : Number(rawPrice);
    if (!Number.isFinite(amount) || amount < 0) return null;
    const currency = rate.currency || fallbackCurrency;
    let price;
    try {
      price = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (_) {
      return null;
    }
    const name = String(rate.presentment_name || rate.name || 'Shipping');
    return `${name}: ${amount === 0 ? 'Free (' + price + ')' : price}`;
  }

  class SF76ShippingEstimator extends (globalThis.HTMLElement || class {}) {
    connectedCallback() {
      if (this.onSubmit) return;
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-shipping-status]');
      this.results = this.querySelector('[data-shipping-results]');
      this.button = this.form?.querySelector('[type="submit"]');
      if (!this.form || !this.status || !this.results || !this.button) return;
      this.sequence = this.sequence || 0;
      this.onSubmit = this.submit.bind(this);
      this.onAddressChange = () => this.invalidate();
      this.onCartChange = () => this.invalidate();
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('input', this.onAddressChange);
      this.form.addEventListener('change', this.onAddressChange);
      this.countryField = this.form.querySelector('[name="address[country]"], [name="shipping_address[country]"]');
      this.provinceInitialized = false;
      this.onCountryChange = () => {
        this.invalidate();
        // CountryProvinceForm replaces its options in its target change listener.
        // Capture observes its non-bubbling initial change; defer until it finishes.
        queueMicrotask(() => {
          if (this.isConnected) this.resetProvince();
        });
      };
      this.countryField?.addEventListener('change', this.onCountryChange, true);
      const countryProvince = this.querySelector('country-province');
      if (countryProvince && globalThis.customElements) {
        const onSubmit = this.onSubmit;
        customElements.whenDefined('country-province').then(() => {
          if (this.isConnected && this.onSubmit === onSubmit && !this.provinceInitialized) this.resetProvince();
        });
      }
      document.addEventListener('cart:refresh', this.onCartChange);
      const pubsub = globalThis.FoxTheme?.pubsub;
      if (pubsub) this.unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartChange);
      this.setBusy(false);
    }

    disconnectedCallback() {
      this.invalidate(false);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('input', this.onAddressChange);
      this.form?.removeEventListener('change', this.onAddressChange);
      this.countryField?.removeEventListener('change', this.onCountryChange, true);
      document.removeEventListener('cart:refresh', this.onCartChange);
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.onSubmit = null;
    }

    resetProvince() {
      const province = this.form.querySelector('select[name="address[province]"], select[name="shipping_address[province]"]');
      if (!province) return;
      const visible = !province.closest('.form-field')?.hidden;
      const initial = !this.provinceInitialized ? province.closest('country-province')?.dataset.province : '';
      this.provinceInitialized = true;
      province.required = visible;
      if (!visible) {
        province.value = '';
        return;
      }
      let placeholder = Array.from(province.options).find((option) => option.value === '');
      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.value = '';
        province.prepend(placeholder);
      }
      placeholder.textContent = 'Select state / province';
      const provided = initial && Array.from(province.options).find((option) =>
        option.value === initial || option.textContent === initial);
      province.value = provided ? provided.value : '';
    }

    setBusy(busy) {
      this.pending = busy;
      this.button.disabled = busy;
      this.setAttribute('aria-busy', String(busy));
    }

    invalidate(announce = true) {
      this.sequence += 1;
      this.controller?.abort();
      this.controller = null;
      if (!this.results || !this.status || !this.button) return;
      const hadEstimate = this.pending || this.results.childElementCount > 0 || this.status.textContent;
      this.results.replaceChildren();
      this.status.textContent = announce && hadEstimate ? messages.changed : '';
      this.setBusy(false);
    }

    async submit(event) {
      event.preventDefault();
      if (this.pending || !this.form.reportValidity()) return;
      this.invalidate(false);
      const sequence = this.sequence;
      const data = new FormData(this.form);
      const address = Object.fromEntries(['country', 'province', 'zip'].map((field) =>
        [field, data.get(`shipping_address[${field}]`) ?? data.get(`address[${field}]`) ?? '']));
      const provinceField = this.form.querySelector('[name="address[province]"], [name="shipping_address[province]"]');
      if (provinceField?.closest('.form-field')?.hidden) address.province = '';
      this.controller = new AbortController();
      this.setBusy(true);
      this.status.textContent = messages.loading;
      try {
        const rates = await getShippingRates({ cartUrl: this.dataset.cartUrl, address,
          origin: location.origin, signal: this.controller.signal });
        if (sequence !== this.sequence || !this.isConnected) return;
        const labels = rates.map((rate) => formatRate(rate, this.dataset.currency, document.documentElement.lang || 'en'));
        if (labels.some((label) => label === null)) throw new Error(messages.error);
        const nodes = labels.map((label) => {
          const item = document.createElement('li');
          item.textContent = label;
          return item;
        });
        this.results.replaceChildren(...nodes);
        this.status.textContent = rates.length
          ? `${rates.length === 1 ? '1 shipping option' : rates.length + ' shipping options'} found. Final shipping and any taxes are confirmed at checkout.`
          : messages.empty;
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected || error.name === 'AbortError') return;
        this.results.replaceChildren();
        this.status.textContent = error.name === 'ShippingTimeoutError' ? messages.timeout
          : error.name === 'ShippingRequestError' ? error.message : messages.error;
      } finally {
        if (sequence === this.sequence) {
          this.controller = null;
          this.setBusy(false);
        }
      }
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { endpoint, getShippingRates, formatRate, SF76ShippingEstimator, messages };
  }
  if (globalThis.customElements && !customElements.get('sf76-shipping-estimator')) {
    customElements.define('sf76-shipping-estimator', SF76ShippingEstimator);
  }
})();
